import { counted } from '../composites/countedUnit';
import { EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME, HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME, OFFER_LETTER_FILE_TYPE_NAME } from './seededFileTypes';
/**
 * The NDA agreement (piece 9; decisions 0068, 0071, 0072): two independent
 * effects gated by status, a questionnaire whose answers set every field, six
 * of eight questions blocking activation, expiry that closes release and never
 * relaxes cover, and overlap raised as a question rather than resolved.
 */
export type NdaStatus = 'Draft' | 'Demanded' | 'Active' | 'Expired' | 'Revoked';
export type NdaScopeType = 'Person' | 'Company' | 'Deal';

export interface AgreementRow {
  id: string;
  scopeType: NdaScopeType;
  scopePersonId: string | null;
  counterpartyCompanyId: string | null;
  scopeDealId: string | null;
  fileId: string | null;
  status: NdaStatus;
  effectiveDate: Date;
  expiryDate: Date | null;
  enablesRelease: boolean;
  requiresCover: boolean;
  obligationEndedAt: Date | null;
  permittedRecipientCompanyIds: readonly string[];
  survivalMonths: number | null;
}

export interface NdaQuestion {
  number: number;
  key: string;
  prompt: string;
  /** A question whose wrong answer causes a disclosure is required; hygiene is not. */
  required: boolean;
  sets: readonly string[];
}

export const NDA_QUESTIONS: readonly NdaQuestion[] = [
  { number: 1, key: 'counterparty', prompt: 'Who is the counterparty?', required: true, sets: ['counterpartyCompanyId'] },
  { number: 2, key: 'requiresCover', prompt: 'Does it require our people to be covered before they open the account’s documents?', required: true, sets: ['requiresCover'] },
  { number: 3, key: 'enablesRelease', prompt: 'Does it permit us to release our documents to them?', required: true, sets: ['enablesRelease'] },
  { number: 4, key: 'scope', prompt: 'Does it cover one Deal or every Deal of the Company?', required: true, sets: ['scopeType', 'scopeDealId'] },
  { number: 5, key: 'dates', prompt: 'When does it take effect, and when does it expire?', required: true, sets: ['effectiveDate', 'expiryDate'] },
  { number: 6, key: 'survival', prompt: 'How long do the obligations survive after expiry?', required: false, sets: ['survivalMonths'] },
  { number: 7, key: 'permittedRecipients', prompt: 'Which other companies may receive documents under it?', required: true, sets: ['permittedRecipientCompanyIds'] },
  { number: 8, key: 'returnOrDestroy', prompt: 'Does it require documents to be returned or destroyed at the end?', required: false, sets: [] },
];

export const BLOCKING_QUESTIONS: readonly number[] = NDA_QUESTIONS.filter((q) => q.required).map((q) => q.number);

/**
 * 🔒 The employment question set (decision 9680; T3368): what an employment form's upload asks beside the file. The
 * numbers run on from the counterparty's eight, so no counterparty effect is ever derived from them, and question 8,
 * returned or destroyed, is the counterparty's own question reused. The answers are confirmed by the uploader and kept
 * as the agreement's `NDAReview` rows.
 */
export const EMPLOYMENT_QUESTION_DATE_SIGNED = 9;
export const EMPLOYMENT_QUESTION_CONFIDENTIALITY = 10;
export const EMPLOYMENT_QUESTION_NON_SOLICIT = 11;
export const EMPLOYMENT_QUESTION_RETURN_OR_DESTROY = 8;

export const EMPLOYMENT_QUESTIONS: readonly NdaQuestion[] = [
  { number: EMPLOYMENT_QUESTION_DATE_SIGNED, key: 'dateSigned', prompt: 'When was it signed?', required: true, sets: ['effectiveDate'] },
  { number: EMPLOYMENT_QUESTION_CONFIDENTIALITY, key: 'confidentiality', prompt: 'Does it carry a confidentiality clause?', required: true, sets: ['scopeType', 'scopePersonId'] },
  { number: EMPLOYMENT_QUESTION_NON_SOLICIT, key: 'nonSolicit', prompt: 'Does it carry a non-solicit, and for how many months after the employment ends?', required: true, sets: [] },
  NDA_QUESTIONS.find((q) => q.number === EMPLOYMENT_QUESTION_RETURN_OR_DESTROY)!,
];

/** 🔒 The question numbers each seeded employment form asks, keyed by the type's name; a marked type absent here asks the date signed alone. */
export const EMPLOYMENT_QUESTION_SETS: Readonly<Record<string, readonly number[]>> = {
  [EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME]: [EMPLOYMENT_QUESTION_DATE_SIGNED, EMPLOYMENT_QUESTION_CONFIDENTIALITY, EMPLOYMENT_QUESTION_NON_SOLICIT, EMPLOYMENT_QUESTION_RETURN_OR_DESTROY],
  [OFFER_LETTER_FILE_TYPE_NAME]: [EMPLOYMENT_QUESTION_DATE_SIGNED],
  [HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME]: [EMPLOYMENT_QUESTION_DATE_SIGNED],
};

/** The questions an upload of this type asks: the set its name keys, the date signed alone for any other employment form, and none for a type that is not one. */
export function employmentQuestionsFor(typeName: string, employmentForm: boolean): readonly NdaQuestion[] {
  const key = Object.keys(EMPLOYMENT_QUESTION_SETS).find((name) => name.toLowerCase() === typeName.trim().toLowerCase());
  if (!key && !employmentForm) return [];
  const numbers = key ? EMPLOYMENT_QUESTION_SETS[key]! : [EMPLOYMENT_QUESTION_DATE_SIGNED];
  return numbers.map((n) => EMPLOYMENT_QUESTIONS.find((q) => q.number === n)!);
}

/** Whether an upload of this type writes an agreement on a Yes: only a type whose questions ask the confidentiality clause. */
export function employmentFormWritesAgreement(typeName: string, employmentForm: boolean): boolean {
  return employmentQuestionsFor(typeName, employmentForm).some((q) => q.number === EMPLOYMENT_QUESTION_CONFIDENTIALITY);
}

/** The answers an employment form's upload carries beside its date signed, as the window sends them. */
export interface EmploymentAnswers {
  confidentiality?: boolean | null;
  nonSolicit?: boolean | null;
  nonSolicitMonths?: number | null;
  returnOrDestroy?: boolean | null;
}

/** 🔒 The multipart field the answers ride in, a JSON object beside the form date. */
export const EMPLOYMENT_ANSWERS_FIELD = 'employmentAnswers';

/** Reads the field as sent, keeping only answers of the right shape; anything else reads as unanswered. */
export function employmentAnswersOf(value: unknown): EmploymentAnswers {
  let raw: unknown = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value) as unknown;
    } catch {
      // silent: a field that is no JSON reads as no answers, and the questions' own refusal names what is missing.
      raw = null;
    }
  }
  const rec = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const yesNo = (v: unknown): boolean | null => (v === true ? true : v === false ? false : null);
  const months = typeof rec.nonSolicitMonths === 'number' && Number.isFinite(rec.nonSolicitMonths) ? rec.nonSolicitMonths : null;
  return { confidentiality: yesNo(rec.confidentiality), nonSolicit: yesNo(rec.nonSolicit), nonSolicitMonths: months, returnOrDestroy: yesNo(rec.returnOrDestroy) };
}

/** 🔒 The longest non-solicit the window takes, ten years; a longer one is a typing slip. */
export const NON_SOLICIT_MAX_MONTHS = 120;

/**
 * 🔒 Why an employment form's answers are refused, in one sentence, or null where they stand (decision 9680). The date
 * signed is the form date, checked by the standard form's own rule; this checks the questions the type asks beyond it.
 * An upload that carries no answers at all is a path with nobody to ask, and stands.
 */
export function employmentAnswersProblem(questions: readonly NdaQuestion[], answers: EmploymentAnswers, sent: boolean): string | null {
  const asks = (n: number) => questions.some((q) => q.number === n);
  if (!sent) return null;
  if (asks(EMPLOYMENT_QUESTION_CONFIDENTIALITY) && (answers.confidentiality === null || answers.confidentiality === undefined)) return 'Answer whether the agreement carries a confidentiality clause.';
  if (asks(EMPLOYMENT_QUESTION_NON_SOLICIT)) {
    if (answers.nonSolicit === null || answers.nonSolicit === undefined) return 'Answer whether the agreement carries a non-solicit.';
    const months = answers.nonSolicitMonths;
    if (answers.nonSolicit && (months === null || months === undefined || !Number.isInteger(months) || months < 1 || months > NON_SOLICIT_MAX_MONTHS)) return `The non-solicit runs a whole number of months after the employment ends, from 1 to ${NON_SOLICIT_MAX_MONTHS}.`;
  }
  return null;
}

/** 🔒 The review rows an employment agreement holds, each its confirmed answer, in the shapes the agreement's page reads. */
export function employmentReviewAnswers(dateSigned: string, answers: EmploymentAnswers): { questionNumber: number; confirmedAnswer: unknown }[] {
  return [
    { questionNumber: EMPLOYMENT_QUESTION_DATE_SIGNED, confirmedAnswer: { dateSigned } },
    { questionNumber: EMPLOYMENT_QUESTION_CONFIDENTIALITY, confirmedAnswer: answers.confidentiality === true },
    { questionNumber: EMPLOYMENT_QUESTION_NON_SOLICIT, confirmedAnswer: { answer: answers.nonSolicit === true, months: answers.nonSolicit === true ? (answers.nonSolicitMonths ?? null) : null } },
    { questionNumber: EMPLOYMENT_QUESTION_RETURN_OR_DESTROY, confirmedAnswer: answers.returnOrDestroy ?? null },
  ];
}

/** Whether an agreement's review rows are the employment set rather than the counterparty's eight. */
export function isEmploymentReviewSet(reviews: readonly { questionNumber: number }[]): boolean {
  return reviews.some((r) => r.questionNumber === EMPLOYMENT_QUESTION_DATE_SIGNED);
}

export interface ReviewRow {
  questionNumber: number;
  detectedAnswer: unknown | null;
  confirmedAnswer: unknown | null;
}

/** The question numbers still blocking activation: 1 to 5 and 7 unanswered; 6 defaults to perpetual and 8 is optional. */
export function activationBlockers(reviews: readonly ReviewRow[]): number[] {
  const answered = new Set(reviews.filter((r) => r.confirmedAnswer !== null && r.confirmedAnswer !== undefined).map((r) => r.questionNumber));
  return BLOCKING_QUESTIONS.filter((n) => !answered.has(n));
}

/** A detected answer is never pre-confirmed: the confirmed column takes only what a person wrote. */
export function confirmedFrom(review: ReviewRow): unknown | null {
  return review.confirmedAnswer ?? null;
}

export interface DerivedEffects {
  counterpartyCompanyId?: string | null;
  requiresCover?: boolean;
  enablesRelease?: boolean;
  scopeType?: NdaScopeType;
  scopeDealId?: string | null;
  effectiveDate?: Date;
  expiryDate?: Date | null;
  survivalMonths?: number | null;
  permittedRecipientCompanyIds?: string[];
  raisesReturnOrDestroy?: boolean;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Every effect is derived from the confirmed answers; nobody sets requiresCover or enablesRelease directly (decision 0072). */
export function deriveEffects(reviews: readonly ReviewRow[]): DerivedEffects {
  const out: DerivedEffects = {};
  for (const r of reviews) {
    const a = confirmedFrom(r);
    if (a === null) continue;
    const rec = asRecord(a);
    switch (r.questionNumber) {
      case 1:
        out.counterpartyCompanyId = typeof rec.companyId === 'string' ? rec.companyId : null;
        break;
      case 2:
        out.requiresCover = a === true || rec.answer === true;
        break;
      case 3:
        out.enablesRelease = a === true || rec.answer === true;
        break;
      case 4:
        out.scopeType = rec.scopeType === 'Deal' ? 'Deal' : rec.scopeType === 'Person' ? 'Person' : 'Company';
        out.scopeDealId = typeof rec.dealId === 'string' ? rec.dealId : null;
        break;
      case 5:
        if (typeof rec.effectiveDate === 'string') out.effectiveDate = new Date(rec.effectiveDate);
        out.expiryDate = typeof rec.expiryDate === 'string' ? new Date(rec.expiryDate) : null;
        break;
      case 6:
        out.survivalMonths = typeof rec.months === 'number' && Number.isInteger(rec.months) && rec.months >= 0 ? rec.months : null;
        break;
      case 7:
        out.permittedRecipientCompanyIds = Array.isArray(rec.companyIds) ? rec.companyIds.filter((c): c is string => typeof c === 'string') : [];
        break;
      case 8:
        out.raisesReturnOrDestroy = a === true || rec.answer === true;
        break;
      default:
        break;
    }
  }
  return out;
}

function onOrAfter(a: Date, b: Date): boolean {
  return a.getTime() >= b.getTime();
}

/** Release is enabled by Active alone, and only inside effectiveDate … expiryDate. */
export function releaseEnabledOn(agreement: Pick<AgreementRow, 'status' | 'enablesRelease' | 'effectiveDate' | 'expiryDate'>, onDate: Date): boolean {
  if (agreement.status !== 'Active' || !agreement.enablesRelease) return false;
  if (!onOrAfter(onDate, agreement.effectiveDate)) return false;
  if (agreement.expiryDate !== null && onDate.getTime() > agreement.expiryDate.getTime() + 86_399_999) return false;
  return true;
}

/** Cover is required by Active or Demanded; expiry never relaxes it, and only the audited obligationEndedAt lifts it. */
export function coverRequiredBy(agreement: Pick<AgreementRow, 'status' | 'requiresCover' | 'obligationEndedAt'>): boolean {
  if (agreement.obligationEndedAt !== null) return false;
  return agreement.requiresCover && (agreement.status === 'Active' || agreement.status === 'Demanded');
}

/** The agreements whose scope covers a record's Company or Deal; a person-scoped agreement covers no record. */
export function agreementsCovering<A extends AgreementRow>(agreements: readonly A[], record: { companyId: string | null; dealId: string | null }): A[] {
  return agreements.filter((a) => {
    if (a.scopeType === 'Company') return a.counterpartyCompanyId !== null && a.counterpartyCompanyId === record.companyId;
    if (a.scopeType === 'Deal') return a.scopeDealId !== null && a.scopeDealId === record.dealId;
    return false;
  });
}

/** A WEA employee is covered on an account by holding their own current person-scoped Active agreement. */
export function personCovered(personAgreements: readonly Pick<AgreementRow, 'scopeType' | 'scopePersonId' | 'status' | 'effectiveDate' | 'expiryDate'>[], personId: string, onDate: Date): boolean {
  return personAgreements.some((a) => a.scopeType === 'Person' && a.scopePersonId === personId && a.status === 'Active' && onOrAfter(onDate, a.effectiveDate) && (a.expiryDate === null || onDate.getTime() <= a.expiryDate.getTime() + 86_399_999));
}

/** The counterparty and every company question 7 named; a counterparty is never locked out of its own documents. */
export function permittedRecipient(agreement: Pick<AgreementRow, 'counterpartyCompanyId' | 'permittedRecipientCompanyIds'>, companyId: string): boolean {
  return agreement.counterpartyCompanyId === companyId || agreement.permittedRecipientCompanyIds.includes(companyId);
}

/**
 * Two agreements overlap when they name one counterparty and their scopes
 * meet: both Company, both the same Deal, or one Company and one Deal of that
 * Company. Overlap is raised as a question, never resolved automatically.
 */
export function scopesOverlap(a: AgreementRow, b: AgreementRow, dealCompanyId: (dealId: string) => string | null): boolean {
  if (a.id === b.id) return false;
  if (a.scopeType === 'Person' || b.scopeType === 'Person') return a.scopeType === 'Person' && b.scopeType === 'Person' && a.scopePersonId === b.scopePersonId;
  if (a.counterpartyCompanyId === null || a.counterpartyCompanyId !== b.counterpartyCompanyId) return false;
  if (a.scopeType === 'Company' && b.scopeType === 'Company') return true;
  if (a.scopeType === 'Deal' && b.scopeType === 'Deal') return a.scopeDealId === b.scopeDealId;
  const deal = a.scopeType === 'Deal' ? a.scopeDealId : b.scopeDealId;
  return deal !== null && dealCompanyId(deal) === a.counterpartyCompanyId;
}

export const DEMANDED_REVIEW_DAYS = 30;

/** A Demanded agreement with no signed file after 30 days is raised for review. */
export function demandedStale(agreement: Pick<AgreementRow, 'status' | 'fileId' | 'effectiveDate'>, now: Date, days = DEMANDED_REVIEW_DAYS): boolean {
  return agreement.status === 'Demanded' && agreement.fileId === null && now.getTime() - agreement.effectiveDate.getTime() >= days * 86_400_000;
}

/** Revoked needs a reason; Expired is the calendar and does not. */
export function statusChangeProblems(to: NdaStatus, reason: string | null): string[] {
  return to === 'Revoked' && (reason === null || reason.trim().length === 0) ? ['reason'] : [];
}

/** "Activating this makes 5 already-released documents visible to Panel Co", and the colleague count when it is known. */
export function activationPreview(facts: { counterpartyName: string; releasedDocumentCount: number; colleagues?: { seeing: number; covered: number } | null }): string[] {
  const lines = [`Activating this makes ${counted(facts.releasedDocumentCount, 'already-released document')} visible to ${facts.counterpartyName}`];
  if (facts.colleagues) lines.push(`${facts.colleagues.seeing} people currently see this account's documents; ${facts.colleagues.covered} are covered`);
  return lines;
}
